import { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem,
  ListItemButton, ListItemText, Tooltip, Typography,
} from "@mui/material";
import { QuoteDraftSummary } from "../../shared/quoteDraft";
import InlineConfirm from "../InlineConfirm/InlineConfirm";

type Props = {
  open: boolean;
  drafts: QuoteDraftSummary[];
  // Who/what this draft is for - "Acme Corp", "Acme Corp (prospect)", or "CSP-CP-1234 - Acme Corp"
  // for an in-progress edit of an existing quote. Resolved by the caller, which already has the
  // customer/quote lists loaded - keeps this dialog itself just a presentational list.
  resolveSubject: (draft: QuoteDraftSummary) => string;
  onClose: () => void;
  onRestore: (draft: QuoteDraftSummary) => void;
  onDelete: (draft: QuoteDraftSummary) => void;
  onClearAll: () => void;
};

// Every local (never-sent-to-the-server) quote draft, with per-row Restore/Delete and a Clear All -
// replaces a one-shot "restore the most recent draft?" popup with a real management surface, so a
// draft is never unrecoverable just because that popup was dismissed or missed (QuoteListPage still
// shows that popup too, as a shortcut into this same dialog for the single most recent draft).
// Issue: Restore and Delete were two small icon buttons right next to each other, and Delete had no
// confirm - one misclick destroyed a draft with no undo. Now the whole row is the Restore target
// (non-destructive) and the trash icon asks to confirm inline, same idiom as Clear All.
const DraftsDialog = ({ open, drafts, resolveSubject, onClose, onRestore, onDelete, onClearAll }: Props) => {
  const [confirmingClearAll, setConfirmingClearAll] = useState(false);
  // Row key (`${quoteType}:${draftId}`) of the one draft currently asking "Delete this draft?".
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const clearAll = () => {
    onClearAll();
    setConfirmingClearAll(false);
  };
  const deleteDraft = (draft: QuoteDraftSummary) => {
    onDelete(draft);
    setConfirmingDelete(null);
  };

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="drafts-dialog-title">
    <DialogTitle id="drafts-dialog-title">Drafts</DialogTitle>
    <DialogContent dividers sx={{ p: 0 }}>
      {drafts.length === 0
        ? <Typography sx={{ p: 2 }} color="text.secondary">No local drafts.</Typography>
        : <List disablePadding>
          {drafts.map((draft, index) => {
            const key = `${draft.quoteType}:${draft.draftId}`;
            const subject = resolveSubject(draft);
            const confirming = confirmingDelete === key;
            return <Box key={key}>
              {index > 0 && <Divider component="li" />}
              <ListItem disablePadding sx={confirming ? { "& .MuiListItemButton-root": { pr: 34 } } : undefined}
                secondaryAction={confirming
                  ? <InlineConfirm prompt="Delete this draft?" confirmLabel="Delete" size="small" sx={{ width: "auto" }}
                    onCancel={() => setConfirmingDelete(null)} onConfirm={() => deleteDraft(draft)} />
                  : <Tooltip title="Delete this draft.">
                    <IconButton edge="end" aria-label="Delete this draft."
                      onClick={() => { setConfirmingClearAll(false); setConfirmingDelete(key); }}><DeleteOutlineIcon /></IconButton>
                  </Tooltip>}>
                {/* Empty title while confirming - otherwise a tooltip already open from hovering the row stays stuck over the confirm. */}
                <Tooltip title={confirming ? "" : "Restore this draft."} enterDelay={800} enterNextDelay={800}>
                  <ListItemButton aria-label={`Restore draft: ${subject}`} disabled={confirming} onClick={() => { if (!confirming) onRestore(draft); }}>
                    <ListItemText
                      primary={subject}
                      secondary={<>
                        {draft.name || "(untitled)"}
                        <br />
                        Autosaved {new Date(draft.updatedAt).toLocaleString()}
                      </>}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </Box>;
          })}
        </List>}
    </DialogContent>
    <DialogActions>
      {confirmingClearAll
        ? <InlineConfirm prompt={`Delete all ${drafts.length} local drafts?`} confirmLabel="Delete All" sx={{ pl: 2 }}
          onCancel={() => setConfirmingClearAll(false)} onConfirm={clearAll} />
        : <>
          {drafts.length > 0 && <Button color="error" sx={{ mr: "auto" }} onClick={() => { setConfirmingDelete(null); setConfirmingClearAll(true); }}>Clear All</Button>}
          <Button variant="contained" onClick={onClose}>Close</Button>
        </>}
    </DialogActions>
  </Dialog>;
};

export default DraftsDialog;
